import React, { useEffect, useState } from "react";
import { Modal } from "rsuite";
import { Equipament, EquipamentHistory } from "../../../../interfaces/Equipament";
import api from "../../../../services/axios";

interface ModalHistoricoProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  computer: Equipament | null;
}

const ModalHistorico: React.FC<ModalHistoricoProps> = ({
  open,
  setOpen,
  computer,
}) => {
  const [history, setHistory] = useState<EquipamentHistory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (open && computer) {
      setLoading(true);
      setError(null);
      api
        .get<EquipamentHistory[]>(`/EquipamentHistory/${computer.id}`)
        .then((res) => {
          const sorted = [...res.data].sort(
            (a, b) =>
              new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
          );
          setHistory(sorted);
        })
        .catch(() => {
          setError("Erro ao carregar histórico.");
          setHistory([]);
        })
        .finally(() => setLoading(false));
    } else {
      setHistory([]);
      setError(null);
    }
  }, [open, computer]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Atual";
    return new Date(dateString).toLocaleDateString("pt-BR");
  };

  if (!computer) return null;

  return (
    <Modal open={open} size="lg" onClose={handleClose}>
      <Modal.Header>
        <Modal.Title>
          <b className="text-lg font-extrabold text-navy-700 dark:text-white">
            Histórico de Uso - {computer.model} ({computer.serieNumber})
          </b>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "400px", overflow: "auto" }}>
        {loading ? (
          <div className="py-8 text-center text-sm text-gray-700 dark:text-gray-300">
            Carregando histórico...
          </div>
        ) : error ? (
          <div className="py-8 text-center text-sm font-semibold text-red-600 dark:text-red-400">{error}</div>
        ) : history.length === 0 ? (
          <div className="py-8 text-center text-sm text-gray-700 dark:text-gray-300">
            Nenhum histórico de uso registrado
          </div>
        ) : (
          <div className="relative">
            {/* Timeline */}
            <div className="absolute left-4 top-0 h-full w-0.5 bg-gray-200 dark:bg-navy-700" aria-hidden="true"></div>

            <div className="space-y-4">
              {history.map((entry) => (
                <div key={entry.id} className="relative flex items-start pl-10">
                  {/* Timeline dot */}
                  <div
                    className={`absolute left-2.5 top-4 h-3 w-3 rounded-full ring-2 ring-white dark:ring-navy-800 ${
                      entry.endDate === null ? "bg-emerald-500" : "bg-gray-400"
                    }`}
                    aria-hidden="true"
                  ></div>

                  {/* Card */}
                  <div
                    className={`w-full rounded-xl border p-4 ${
                      entry.endDate === null
                        ? "border-emerald-200 bg-emerald-50 dark:border-emerald-500/30 dark:bg-emerald-900/20"
                        : "border-gray-100 bg-gray-50 dark:border-white/10 dark:bg-navy-900/40"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        {/* Sem nome = período em que a máquina ficou disponível. O fallback antigo
                            caía num `userId` que nunca foi preenchido — e, se fosse, imprimiria um
                            identificador na cara do usuário. */}
                        <p className="font-bold text-navy-700 dark:text-white">
                          {entry.userName || "Disponível"}
                        </p>
                        <p className="text-sm tabular-nums text-gray-700 dark:text-gray-300">
                          {formatDate(entry.startDate)} -{" "}
                          {formatDate(entry.endDate)}
                        </p>
                      </div>
                      {entry.endDate === null && (
                        <span className="shrink-0 rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                          {entry.userName ? "Período atual" : "Disponível"}
                        </span>
                      )}
                    </div>

                    {entry.notes && (
                      <p className="mt-2 text-xs italic text-gray-700 dark:text-gray-300">
                        {entry.notes}
                      </p>
                    )}

                    {/* Duration */}
                    <div className="mt-2 text-xs font-medium text-gray-700 dark:text-gray-300">
                      Duração: {calculateDuration(entry.startDate, entry.endDate)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button
          type="button"
          onClick={handleClose}
          className="flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] dark:border-white/15 dark:text-gray-200 dark:hover:bg-navy-700"
        >
          Fechar
        </button>
      </Modal.Footer>
    </Modal>
  );
};

function calculateDuration(startDate: string, endDate: string | null): string {
  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : new Date();
  const diffTime = Math.abs(end.getTime() - start.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 30) {
    return `${diffDays} dia${diffDays !== 1 ? "s" : ""}`;
  } else if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return `${months} ${months !== 1 ? "meses" : "mês"}`;
  } else {
    const years = Math.floor(diffDays / 365);
    const remainingMonths = Math.floor((diffDays % 365) / 30);
    return `${years} ano${years !== 1 ? "s" : ""}${
      remainingMonths > 0 ? ` e ${remainingMonths} ${remainingMonths !== 1 ? "meses" : "mês"}` : ""
    }`;
  }
}

export default ModalHistorico;
